/**
 * CifCompareModal — Existing-CIF Compare & Merge surface (Bill approved mockup
 * + defaults 2026-06-12).
 *
 * Replaces the bare DuplicateCifPrompt (whole body). When PeopleStep's silent
 * CustSrch surfaces a possible existing CIF — early (SSN reached 9 digits) or
 * at-Add (SSN strong / name+DOB fallback) — this modal opens INSTEAD of the
 * old lose-everything prompt. It shows the entered draft vs the on-file CIF
 * side-by-side so the FSR decides informed, and BOTH outcomes preserve every
 * session artifact (captures, OFAC, AML).
 *
 * LOCKED DESIGN:
 *   docs/operations/person-modal-inventory/09-EXISTING-CIF-COMPARE-MOCKUP.html
 *
 * Bill's confirm-box answers (defaults, approved 2026-06-12):
 *   (1) Pilot update scope = CONTACT-FAMILY FIELDS ONLY (physical/mailing
 *       address, phones, email, employment). Name + DOB changes are OUT of this
 *       surface (their own gated dialogs). Identity rows are display-only here.
 *   (2) SSN-less matching tightened to NAME + DOB (PeopleStep).
 *   (3) "Not a match" audit = a SessionEvent (emitted by PeopleStep).
 *   (4) The session's photo-ID capture SATISFIES change-of-contact verification
 *       (verification method pre-filled to Photo ID; the capture is the doc).
 *
 * HARD CAVEAT (Core DB integrity): a NEW CIF may NEVER be added with a tax id
 * that matches an existing CIF (the CIF number is a Core key). When the match
 * was ON the tax id and the FSR chooses "Not a match", PeopleStep FORCE-CLEARS
 * the entered SSN/EIN — this modal surfaces that explicitly in the banner +
 * not-a-match panel.
 *
 * Ground-in-code: data-loss line replaced = PeopleStep.tsx onDupReviewExisting
 * (pick(row) then discards dupGuard.person). Hydration path reused =
 * useCustomerPick.pick (CustInq → useInquiryHydration, every field). Change-of-
 * contact ceremony reused = RCM/maint-actions/address-change useAddressChange +
 * VERIFICATION_METHODS + custAcctRelInq cascade.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banner, Button, ModalShell, type CustomerSearchRow } from '@/components/primitives';
import { useApi } from '../../../../hooks/useApi';
import { useCustomerPick } from '../../../../hooks/useCustomerPick';
import { useAddressChange } from '../../../../../RCM/maint-actions/address-change/useAddressChange';
import { unwrapJxrlArray, txt } from '@/utils/jxrl';
import type { CustModRequest } from '@retail-ops/shared-types';
import type { AcctType } from '@retail-ops/shared-types/enums';
import type { RaoPerson } from '../../../../types';
import { readAttachedDocs, type AttachedDoc } from '../../shared/attachedDocs';
import { AuthorizedByField } from '@/components/customer/AuthorizedByField';
import { authorizedByOk, normalizeAuthorizedBy } from '@/components/customer/authorizedBy';

// --------------------------------------------------------------------------
// Verification-method vocabulary — REUSED verbatim from the change-of-contact
// ceremony (RCM/maint-actions/address-change/AddressChangeDialog.tsx:44-51).
// Codes 0-5, label-for-audit-note. Kept in lockstep with that source.
// --------------------------------------------------------------------------
const VERIFICATION_METHODS: ReadonlyArray<{ code: string; label: string }> = [
  { code: '1', label: 'Photo ID (In Person)' },
  { code: '0', label: 'Post Office Verification' },
  { code: '2', label: 'SSN Verification' },
  { code: '3', label: 'Banno (Online Banking)' },
  { code: '4', label: 'Security Questions' },
  { code: '5', label: 'Transactional Questions' },
];

const ACCT_TYPE_LABEL: Record<string, string> = {
  '10': 'DDA', '20': 'SAV', '30': 'CD', '50': 'SDB', '60': 'IRA',
};

// Contact-family fields eligible for the Use-existing update step (answer #1).
// Identity rows (name / DOB / SSN) are display-only and NOT in this list.
type ContactFieldKey =
  | 'physicalAddress'
  | 'mailingAddress'
  | 'phoneCell'
  | 'phoneHome'
  | 'phoneBus'
  | 'email'
  | 'employment';

interface CompareRow {
  /** Field label for the table. */
  label: string;
  /** Entered value (this session). */
  entered: string;
  /** On-file value (hydrated CIF). */
  onFile: string;
  /** 'identity' rows are display-only; 'contact' rows are updatable. */
  kind: 'identity' | 'contact';
  /** For contact rows — the update-step field key. */
  fieldKey?: ContactFieldKey;
  /** Row state for the State column + highlight. */
  state: 'match' | 'differ' | 'entonly' | 'fileonly';
}

export interface CifCompareModalProps {
  open: boolean;
  cartId: string;
  /** The entered draft (carries extra.attachedDocs / extra.ofacStatus / AML). */
  draft: RaoPerson;
  /** On-file candidate rows from the silent CustSrch (filtered to non-cart CIFs). */
  matches: ReadonlyArray<CustomerSearchRow>;
  /** What we matched on — 'tax ID' (collision) or a name/name+DOB description. */
  matchedOn: string;
  /** True when the match was on the entered tax id (forces the clear-SSN path). */
  matchedOnTaxId: boolean;
  /** Officer code for the change-of-contact audit note. Defaults '000'. */
  officerCode?: string;
  /**
   * CHG-0186 (Bill 2026-11-04): "Authorized by (full name)" starts with this — the first customer on an
   * officer-created in-branch application (authorizedByPrefill). Blank on an online application.
   */
  authorizedByPrefill?: string;
  /**
   * Use this existing CIF. Receives the on-file person hydrated + the chosen
   * contact updates + EVERY session artifact carried over from the draft. The
   * host opens the review-add ceremony seeded with this person.
   */
  onUseExisting: (person: RaoPerson) => void;
  /**
   * Not a match — continue adding the entered draft as a NEW CIF. The host
   * emits the "rejected match" SessionEvent and, when matchedOnTaxId, force-
   * clears the entered SSN/EIN before re-add (the caveat). `candidate` is the
   * CIF the FSR rejected (for the audit row).
   */
  onNotAMatch: (candidate: CustomerSearchRow) => void;
  /** Back out — nothing added, the open modal + draft stay untouched. */
  onCancel: () => void;
}

// --------------------------------------------------------------------------
// value helpers
// --------------------------------------------------------------------------
function maskSsn(taxId: string | undefined): string {
  const d = (taxId ?? '').replace(/\D/g, '');
  if (d.length !== 9) return taxId ?? '';
  return `•••-••-${d.slice(5)}`;
}

function fmtAddress(a: { addr?: string; addr2?: string; city?: string; state?: string; zip?: string }): string {
  const line1 = [a.addr, a.addr2].filter(Boolean).join(' ').trim();
  const line2 = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ').trim();
  return [line1, line2].filter(Boolean).join(', ');
}

function fmtMail(p: RaoPerson): string {
  return fmtAddress({ addr: p.mailAddr, addr2: p.mailAddr2, city: p.mailCity, state: p.mailState, zip: p.mailZip });
}

function fmtPhysical(p: RaoPerson): string {
  return fmtAddress({ addr: p.addr, addr2: p.addr2, city: p.city, state: p.state, zip: p.zip });
}

function fmtEmployment(p: RaoPerson): string {
  return [p.employer, p.occupation].filter(Boolean).join(' · ');
}

function rowState(entered: string, onFile: string): CompareRow['state'] {
  const e = entered.trim();
  const f = onFile.trim();
  if (e && f) return e.toLowerCase() === f.toLowerCase() ? 'match' : 'differ';
  if (e && !f) return 'entonly';
  if (!e && f) return 'fileonly';
  return 'match'; // both empty → nothing to show as a diff
}

interface CascadeOption {
  acctId: string;
  acctType: AcctType | string;
  selected: boolean;
}

export function CifCompareModal(props: CifCompareModalProps): JSX.Element | null {
  const {
    open, cartId, draft, matches, matchedOn, matchedOnTaxId, officerCode = '000', authorizedByPrefill = '',
    onUseExisting, onNotAMatch, onCancel,
  } = props;

  const { client, makeSignal } = useApi();
  const { pick } = useCustomerPick();
  const { run: runAddressChange } = useAddressChange();

  // Selected candidate (mockup compares one; supports >1 via a selector).
  const [selectedCifId, setSelectedCifId] = useState<string>(matches[0]?.cifId ?? '');
  const selectedRow = useMemo(
    () => matches.find((m) => m.cifId === selectedCifId) ?? matches[0],
    [matches, selectedCifId],
  );

  // On-file hydrated person (single hydration path — useCustomerPick.pick).
  const [onFile, setOnFile] = useState<RaoPerson | null>(null);
  const [hydrating, setHydrating] = useState<boolean>(false);

  // Decision panel: which rail button is expanded.
  const [decision, setDecision] = useState<'none' | 'use' | 'new'>('none');

  // Update-step state (change-of-contact ceremony, REUSED).
  const [checked, setChecked] = useState<ReadonlySet<ContactFieldKey>>(new Set());
  const [verCode, setVerCode] = useState<string>('1'); // default Photo ID
  const [authorizedBy, setAuthorizedBy] = useState<string>(authorizedByPrefill);
  const [cascade, setCascade] = useState<ReadonlyArray<CascadeOption>>([]);
  const [applying, setApplying] = useState<boolean>(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Session photo-ID capture → satisfies change-of-contact verification (#4).
  const sessionDocs = useMemo<ReadonlyArray<AttachedDoc>>(() => readAttachedDocs(draft), [draft]);
  const hasPhotoIdCapture = useMemo(
    () => sessionDocs.some((d) => d.docCode === 'GOVT_ID' || /id|license|passport/i.test(d.docCode)),
    [sessionDocs],
  );

  const ofacStatus = ((draft.extra ?? {}) as Record<string, unknown>)['ofacStatus'] as string | undefined;
  const amlScore = draft.amlInsightScore;

  // Hydrate the on-file CIF when the selection changes (or the modal opens).
  const lastHydratedRef = useRef<string>('');
  useEffect(() => {
    if (!open || !selectedRow) return;
    if (lastHydratedRef.current === selectedRow.cifId) return;
    lastHydratedRef.current = selectedRow.cifId;
    setHydrating(true);
    setOnFile(null);
    void pick(selectedRow, draft.role)
      .then((p) => { setOnFile(p); })
      .finally(() => { setHydrating(false); });
  }, [open, selectedRow, pick, draft.role]);

  // Pre-fill verification + reset update-step on (re)open or candidate change.
  useEffect(() => {
    if (!open) return;
    setVerCode(hasPhotoIdCapture ? '1' : '1');
    setAuthorizedBy(authorizedByPrefill);
    setChecked(new Set());
    setDecision('none');
    setApplyError(null);
  }, [open, selectedCifId, hasPhotoIdCapture, authorizedByPrefill]);

  // Load associated accounts for the cascade (custAcctRelInq) when the FSR
  // expands the Use-existing panel — REUSED from AddressChangeDialog:109-149.
  const cascadeLoadedFor = useRef<string>('');
  useEffect(() => {
    if (decision !== 'use' || !selectedRow) return;
    if (cascadeLoadedFor.current === selectedRow.cifId) return;
    cascadeLoadedFor.current = selectedRow.cifId;
    const signal = makeSignal();
    client.accounts.custAcctRelInq(selectedRow.cifId, { signal }).then(
      (res): void => {
        const raw = res as unknown as Record<string, unknown>;
        const dataNode =
          (raw['data'] as Record<string, unknown> | undefined) ??
          (raw['Data'] as Record<string, unknown> | undefined) ??
          raw;
        const recs = unwrapJxrlArray<Record<string, unknown>>(
          dataNode, 'CustAcctRelInqRecArray', 'CustAcctRelInqRec',
        );
        setCascade(
          recs
            .map((r) => {
              const acctObj = r['AcctId'];
              let acctId = '';
              let acctType = '';
              if (typeof acctObj === 'string') {
                acctId = acctObj;
              } else if (acctObj && typeof acctObj === 'object') {
                const inner = acctObj as Record<string, unknown>;
                acctId = txt(inner['AcctId']);
                acctType = txt(inner['AcctType']);
              }
              if (!acctType) acctType = txt(r['AcctType']);
              return { acctId, acctType };
            })
            .filter((a) => Boolean(a.acctId) && Boolean(a.acctType)
              && a.acctType !== '80' && a.acctType !== '91' && a.acctType !== '92')
            .map((a) => ({ acctId: a.acctId, acctType: a.acctType as AcctType, selected: true })),
        );
      },
      (): void => { /* ignore — usable without cascade */ },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision, selectedRow]);

  // ------------------------------------------------------------------------
  // Build the compare rows once the on-file CIF hydrates.
  // ------------------------------------------------------------------------
  const rows = useMemo<ReadonlyArray<CompareRow>>(() => {
    if (!onFile) return [];
    const r: CompareRow[] = [];
    // Identity rows — DISPLAY-ONLY (answer #1: name/DOB out of this surface).
    r.push({ label: 'Full name', kind: 'identity', entered: draft.fullName || `${draft.firstName ?? ''} ${draft.lastName ?? ''}`.trim(), onFile: onFile.fullName || `${onFile.firstName ?? ''} ${onFile.lastName ?? ''}`.trim(), state: rowState(draft.fullName ?? '', onFile.fullName ?? '') });
    r.push({ label: 'DOB', kind: 'identity', entered: draft.dob ? '••/••/••••' : '', onFile: onFile.dob ? '••/••/••••' : '', state: rowState(draft.dob ?? '', onFile.dob ?? '') });
    r.push({ label: 'SSN / TIN', kind: 'identity', entered: maskSsn(draft.taxId), onFile: maskSsn(onFile.taxId), state: rowState((draft.taxId ?? '').replace(/\D/g, ''), (onFile.taxId ?? '').replace(/\D/g, '')) });
    // Contact-family rows — UPDATABLE.
    const physE = fmtPhysical(draft); const physF = fmtPhysical(onFile);
    r.push({ label: 'Physical address', kind: 'contact', fieldKey: 'physicalAddress', entered: physE, onFile: physF, state: rowState(physE, physF) });
    const mailE = fmtMail(draft); const mailF = fmtMail(onFile);
    if (mailE || mailF) r.push({ label: 'Mailing address', kind: 'contact', fieldKey: 'mailingAddress', entered: mailE, onFile: mailF, state: rowState(mailE, mailF) });
    r.push({ label: 'Cell phone', kind: 'contact', fieldKey: 'phoneCell', entered: draft.phoneCell ?? '', onFile: onFile.phoneCell ?? '', state: rowState(draft.phoneCell ?? '', onFile.phoneCell ?? '') });
    if (draft.phoneHome || onFile.phoneHome) r.push({ label: 'Home phone', kind: 'contact', fieldKey: 'phoneHome', entered: draft.phoneHome ?? '', onFile: onFile.phoneHome ?? '', state: rowState(draft.phoneHome ?? '', onFile.phoneHome ?? '') });
    if (draft.phoneBus || onFile.phoneBus) r.push({ label: 'Business phone', kind: 'contact', fieldKey: 'phoneBus', entered: draft.phoneBus ?? '', onFile: onFile.phoneBus ?? '', state: rowState(draft.phoneBus ?? '', onFile.phoneBus ?? '') });
    r.push({ label: 'Email', kind: 'contact', fieldKey: 'email', entered: draft.email ?? '', onFile: onFile.email ?? '', state: rowState(draft.email ?? '', onFile.email ?? '') });
    const empE = fmtEmployment(draft); const empF = fmtEmployment(onFile);
    if (empE || empF) r.push({ label: 'Employment', kind: 'contact', fieldKey: 'employment', entered: empE, onFile: empF, state: rowState(empE, empF) });
    return r;
  }, [onFile, draft]);

  const counts = useMemo(() => {
    let match = 0, differ = 0, entonly = 0, fileonly = 0;
    for (const r of rows) {
      if (r.state === 'match') match++;
      else if (r.state === 'differ') differ++;
      else if (r.state === 'entonly') entonly++;
      else fileonly++;
    }
    return { match, differ, entonly, fileonly };
  }, [rows]);

  // Updatable rows that actually carry an entered value worth offering.
  const updatableRows = useMemo(
    () => rows.filter((r) => r.kind === 'contact' && r.fieldKey && r.entered.trim() && r.state !== 'match'),
    [rows],
  );

  const toggleField = (k: ContactFieldKey): void => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const toggleCascade = (acctId: string): void => {
    setCascade((prev) => prev.map((c) => (c.acctId === acctId ? { ...c, selected: !c.selected } : c)));
  };
  const selectAllCascade = (selected: boolean): void => {
    setCascade((prev) => prev.map((c) => ({ ...c, selected })));
  };

  const addressFieldChecked = checked.has('physicalAddress') || checked.has('mailingAddress');
  const initialsRequired = verCode !== '0' && verCode !== '3';
  const canApply =
    !applying &&
    checked.size > 0 &&
    authorizedByOk(authorizedBy, initialsRequired);

  // ------------------------------------------------------------------------
  // Build the merged on-file person + carry over EVERY session artifact.
  // Selected contact fields take the ENTERED value; unchecked keep on-file.
  // extra.attachedDocs / ofacStatus / AML travel on the merged draft so review-
  // add shows the session work (the old path seeded the bare hydration only).
  // ------------------------------------------------------------------------
  const buildMergedPerson = useCallback((): RaoPerson => {
    if (!onFile) return draft;
    const merged: RaoPerson = { ...onFile, role: draft.role, dirty: true };
    const apply = (k: ContactFieldKey): boolean => checked.has(k);
    if (apply('physicalAddress')) {
      merged.addr = draft.addr ?? ''; merged.addr2 = draft.addr2 ?? '';
      merged.city = draft.city ?? ''; merged.state = draft.state ?? ''; merged.zip = draft.zip ?? '';
    }
    if (apply('mailingAddress')) {
      merged.mailAddr = draft.mailAddr ?? ''; merged.mailAddr2 = draft.mailAddr2 ?? '';
      merged.mailCity = draft.mailCity ?? ''; merged.mailState = draft.mailState ?? ''; merged.mailZip = draft.mailZip ?? '';
    }
    if (apply('phoneCell')) merged.phoneCell = draft.phoneCell ?? '';
    if (apply('phoneHome')) merged.phoneHome = draft.phoneHome ?? '';
    if (apply('phoneBus')) merged.phoneBus = draft.phoneBus ?? '';
    if (apply('email')) merged.email = draft.email ?? '';
    if (apply('employment')) {
      merged.employer = draft.employer ?? ''; merged.occupation = draft.occupation ?? '';
      if (draft.employmentStatus) merged.employmentStatus = draft.employmentStatus;
    }
    // Carry over the session artifacts (the data-loss fix). Merge extra so the
    // on-file CIP block (idType/idNumber from hydration) is preserved AND the
    // session's captures/OFAC/AML attach as this visit's record.
    merged.extra = {
      ...(onFile.extra ?? {}),
      ...(draft.extra ?? {}),
    };
    if (typeof amlScore === 'number') merged.amlInsightScore = amlScore;
    if (draft.amlInsightReportDate) merged.amlInsightReportDate = draft.amlInsightReportDate;
    if (draft.amlInsightRunBy) merged.amlInsightRunBy = draft.amlInsightRunBy;
    if (draft.amlInsightInquiryId) merged.amlInsightInquiryId = draft.amlInsightInquiryId;
    if (draft.amlInsightExtractId) merged.amlInsightExtractId = draft.amlInsightExtractId;
    return merged;
  }, [onFile, draft, checked, amlScore]);

  // Apply the selected updates through the REUSED change-of-contact ceremony,
  // then hand the merged person to the review-add ceremony.
  const handleApplyUpdates = async (): Promise<void> => {
    if (!canApply || !onFile) return;
    setApplying(true);
    setApplyError(null);
    const verLabel = VERIFICATION_METHODS.find((v) => v.code === verCode)?.label ?? verCode;
    const cifId = onFile.cifId ?? selectedRow?.cifId ?? '';
    try {
      // 1) Address change(s) → useAddressChange.run (CustMod + AcctMod cascade
      //    + CustMsgAdd audit note + session events). REUSED verbatim.
      if (checked.has('physicalAddress')) {
        const onFileState = (onFile.state ?? '').toUpperCase();
        const draftState = (draft.state ?? '').toUpperCase();
        const out = Boolean(onFileState && draftState && onFileState !== draftState);
        const res = await runAddressChange({
          cifId,
          newAddr: draft.addr ?? '',
          ...(draft.addr2 ? { newAddr2: draft.addr2 } : {}),
          newCity: draft.city ?? '',
          newState: (draft.state ?? '').toUpperCase(),
          newZip: draft.zip ?? '',
          verificationCode: verCode,
          verificationLabel: verLabel,
          customerInitials: normalizeAuthorizedBy(authorizedBy),
          officerCode,
          cascadeAccounts: cascade.filter((c) => c.selected).map((c) => ({ acctId: c.acctId, acctType: c.acctType })),
          outOfState: out,
        });
        if (!res.ok) { setApplyError(res.error ?? 'Address update failed'); setApplying(false); return; }
      }
      // 2) CIF-level contact updates (mailing / phone / email / employment) via
      //    CustMod (no account cascade — these are CIF-scoped fields). Field
      //    names per CustModRequest (shared-types/accounts.ts:88+). AuthMethod
      //    maps the verification method to the InPerson/Phone/Mail/Online vocab.
      const authMethod: CustModRequest['AuthMethod'] =
        verCode === '3' ? 'Online' : verCode === '0' ? 'Mail' : 'InPerson';
      const custModFields: Partial<CustModRequest> = {
        CifId: cifId, AuthMethod: authMethod, OffCode: officerCode,
      };
      let hasCifLevel = false;
      if (checked.has('mailingAddress')) {
        custModFields.MailAddr = draft.mailAddr ?? '';
        custModFields.MailCity = draft.mailCity ?? '';
        custModFields.MailState = (draft.mailState ?? '').toUpperCase();
        custModFields.MailZip = draft.mailZip ?? '';
        hasCifLevel = true;
      }
      if (checked.has('phoneCell')) { custModFields.PhoneCell = draft.phoneCell ?? ''; hasCifLevel = true; }
      if (checked.has('phoneHome')) { custModFields.PhoneHome = draft.phoneHome ?? ''; hasCifLevel = true; }
      if (checked.has('phoneBus')) { custModFields.PhoneBus = draft.phoneBus ?? ''; hasCifLevel = true; }
      if (checked.has('email')) { custModFields.Email = draft.email ?? ''; hasCifLevel = true; }
      if (checked.has('employment')) {
        custModFields.EmplName = draft.employer ?? '';
        custModFields.OccType = draft.occupation ?? '';
        if (draft.employmentStatus) custModFields.EmploymentStatus = draft.employmentStatus;
        hasCifLevel = true;
      }
      if (hasCifLevel) {
        // x-pcb-surface: opening — this is the People step's update of an existing customer during account opening, not a
      // change from the customer record, so the record-maintenance test-customer fence does not apply (MaintenanceFence).
      const resp = await client.accounts.custMod(custModFields as CustModRequest, { headers: { 'x-pcb-surface': 'opening' } });
        const env = resp as unknown as { success?: boolean; error?: string };
        if (env?.success === false) { setApplyError(env.error ?? 'CIF update failed'); setApplying(false); return; }
      }
      setApplying(false);
      onUseExisting(buildMergedPerson());
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Update failed');
      setApplying(false);
    }
  };

  // Use existing with NO updates (link only) — straight to review-add.
  const handleLinkOnly = (): void => {
    onUseExisting(buildMergedPerson());
  };

  if (!open) return null;

  const n = matches.length;
  const candidateName = selectedRow?.name ?? '';
  const pendingName = draft.fullName || `${draft.firstName ?? ''} ${draft.lastName ?? ''}`.trim();

  return (
    <ModalShell
      open={open}
      onClose={onCancel}
      title="Possible existing customer — compare before you decide"
      size="xl"
      footer={
        /* ONE-STEP flow (Bill 2026-07-03: "Why the two step process?") — the
           update picker renders inline with the comparison; the pinned footer
           carries the single confirm. Checked rows apply through the
           Change-in-Contact ceremony; zero checked = link as-is. Only
           "Not a match" keeps a confirm leg (it can clear the entered tax id
           — destructive, so it earns one). */
        decision === 'new' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', width: '100%' }}>
            <Button variant="ghost" size="md" onClick={() => setDecision('none')} disabled={applying}>
              ← Back to comparison
            </Button>
            <span style={{ flex: 1 }} />
            <Button
              variant="primary"
              size="md"
              onClick={() => { if (selectedRow) onNotAMatch(selectedRow); }}
              disabled={applying}
            >
              {matchedOnTaxId ? 'Clear tax id & continue adding' : 'Continue adding as new'}
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', width: '100%' }}>
            <Button variant="ghost" size="md" onClick={onCancel} disabled={applying}>Cancel</Button>
            <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>
              Cancel backs out — nothing added, the open modal and its draft stay untouched.
            </span>
            <span style={{ flex: 1 }} />
            <Button
              variant="ghost"
              size="md"
              onClick={() => setDecision('new')}
              disabled={applying}
            >
              Not a match — continue adding
            </Button>
            <Button
              variant="green"
              size="md"
              onClick={() => {
                if (checked.size > 0) void handleApplyUpdates();
                else handleLinkOnly();
              }}
              disabled={applying || hydrating || !onFile || (checked.size > 0 && !canApply)}
              title={
                hydrating || !onFile
                  ? 'Loading the on-file record…'
                  : checked.size > 0 && !authorizedByOk(authorizedBy, initialsRequired)
                    ? 'Enter who authorized the change (full name) under Verification method'
                    : undefined
              }
            >
              {applying
                ? 'Applying…'
                : checked.size > 0
                  ? `Use existing CIF — apply ${checked.size} update${checked.size === 1 ? '' : 's'} →`
                  : 'Use existing CIF as-is →'}
            </Button>
          </div>
        )
      }
    >
      {/* match banner */}
      <Banner tone={matchedOnTaxId ? 'info' : 'warn'}>
        <strong>{pendingName || 'This person'}</strong> (being added now) matches{' '}
        {n === 1 ? '' : `${n} on-file customers, incl. `}
        <strong>CIF #{selectedRow?.cifId} — {candidateName}</strong>{' '}
        — matched on <strong>{matchedOn}</strong>.
        {matchedOnTaxId ? (
          <div style={{ marginTop: 6, fontSize: 12.5 }}>
            This SSN/TIN matches CIF #{selectedRow?.cifId}. If the rows below look like a
            different person, suspect a <strong>typo in the tax ID</strong> — choose
            "Not a match" and you'll be required to correct it before re-adding
            (a new CIF can never share an existing CIF's tax id).
          </div>
        ) : (
          <div style={{ marginTop: 6, fontSize: 12.5 }}>
            No SSN was entered (or it didn't match) — this is a name+DOB candidate, the
            case where this comparison matters most. Confirm against the rows below.
          </div>
        )}
      </Banner>

      {/* candidate selector when >1 match */}
      {n > 1 && (
        <div style={{ marginTop: 'var(--sp-4)' }}>
          <label style={{ fontSize: 11, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
            On-file candidate ({n} possible)
          </label>
          <select
            aria-label="On-file candidate"
            value={selectedCifId}
            onChange={(e) => { setSelectedCifId(e.target.value); }}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--ink-100)', borderRadius: 'var(--r-sm)', fontSize: 13 }}
          >
            {matches.map((m) => (
              <option key={m.cifId} value={m.cifId}>
                CIF #{m.cifId} — {m.name}{m.address ? ` · ${m.address}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* side-by-side compare table */}
      <div style={{ marginTop: 'var(--sp-5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)' }}>
          <h3 style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink-700)' }}>
            Side-by-side — entered vs on file
          </h3>
          {!hydrating && onFile && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-700)', background: 'var(--ink-50)', border: '1px solid var(--ink-100)', borderRadius: 8, padding: '4px 10px' }}>
              <strong>{counts.match}</strong> match · <strong>{counts.differ}</strong> differ · <strong>{counts.entonly}</strong> entered-only · <strong>{counts.fileonly}</strong> on-file-only
            </span>
          )}
        </div>
        {hydrating || !onFile ? (
          <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--ink-600)', fontSize: 13, border: '1px solid var(--ink-100)', borderRadius: 8 }}>
            Loading CIF #{selectedRow?.cifId} from Core Director…
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, border: '1px solid var(--ink-100)', borderRadius: 8, overflow: 'hidden' }}>
            <thead>
              <tr>
                <th style={{ ...thBase, width: 150 }}>Field</th>
                <th style={{ ...thBase, background: 'var(--brand-fill)' }}>Entered — this session</th>
                <th style={thBase}>On file — CIF #{selectedRow?.cifId}</th>
                <th style={{ ...thBase, width: 130 }}>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} style={r.kind === 'identity' ? { background: 'var(--ink-25, #fafbfc)' } : undefined}>
                  <td style={{ ...tdBase, fontWeight: 700, color: 'var(--ink-700)', whiteSpace: 'nowrap' }}>
                    {r.label}
                    {r.kind === 'identity' && (
                      <span style={{ display: 'block', fontSize: 9.5, fontWeight: 600, color: 'var(--ink-500)', letterSpacing: '0.04em' }}>
                        DISPLAY-ONLY
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdBase, ...(r.state === 'differ' ? diffCell : r.state === 'entonly' ? entCell : {}) }}>
                    {r.entered || <span style={mutedStyle}>— not entered —</span>}
                  </td>
                  <td style={{ ...tdBase, ...(r.state === 'differ' ? diffCell : r.state === 'fileonly' ? fileCell : {}) }}>
                    {r.onFile || <span style={mutedStyle}>— none on file —</span>}
                  </td>
                  <td style={tdBase}><StatePill state={r.state} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 8 }}>
          Amber = both values present, pick per-field in the update step · Blue = entered this
          session, missing on file · Gray = on file only (never blanked by this flow). Identity
          rows (name / DOB / SSN) are display-only — name and DOB changes use their own gated dialogs.
        </div>
      </div>

      {/* preserved session artifacts — carried over on EVERY path */}
      <div style={{ marginTop: 'var(--sp-5)', border: '1px solid var(--ok-line)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--ink-50)', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-700)' }}>
          This session's work — carried over no matter what you choose
        </div>
        <div style={{ padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <ArtifactChip ok label={`${sessionDocs.length} document${sessionDocs.length === 1 ? '' : 's'} captured`} />
          {ofacStatus && <ArtifactChip ok={ofacStatus === 'clear' || ofacStatus === 'archived-only'} label={`OFAC — ${ofacStatus}`} />}
          {typeof amlScore === 'number' && <ArtifactChip ok={amlScore >= 750} label={`LexisNexis AML — FraudPoint ${amlScore}`} />}
          <ArtifactChip info label="Draft fields — name · address · contact · employment" />
          <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--ink-700)', marginTop: 4 }}>
            Choosing either path discards nothing. "Use existing CIF" merges the captures, OFAC
            and AML onto the on-file person entering review-add. "Not a match" keeps the full
            entered draft and continues the add. Existing-CIF doctrine holds — linked customers
            do not auto-re-run LN/OFAC; the session's already-run results attach as this visit's record.
          </div>
        </div>
      </div>

      {/* ── USE-EXISTING update panel ── */}
      {decision !== 'new' && onFile && (
        <div style={{ marginTop: 'var(--sp-5)', border: '1.5px solid var(--info-line)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ background: 'var(--brand-fill)', color: 'var(--on-brand)', padding: '9px 14px', fontSize: 12, fontWeight: 700 }}>
            IF YOU USE CIF #{onFile.cifId} — bring this session's entries onto the record (optional, per field)
          </div>
          <div style={{ padding: '13px 14px', display: 'flex', flexDirection: 'column', gap: 12, background: 'var(--surface-card, #fff)' }}>
            {applyError && <Banner tone="err">{applyError}</Banner>}

            {updatableRows.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--ink-600)' }}>
                Nothing entered this session differs from the on-file contact record — link the CIF
                as-is, or go back and adjust the draft.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {updatableRows.map((r) => (
                  <label key={r.fieldKey} style={uprowStyle}>
                    <input
                      type="checkbox"
                      checked={r.fieldKey ? checked.has(r.fieldKey) : false}
                      onChange={() => r.fieldKey && toggleField(r.fieldKey)}
                      style={{ marginTop: 2 }}
                    />
                    <div>
                      <strong>{r.label}</strong>{' '}
                      <span style={{ color: 'var(--ink-600)' }}>{r.onFile || 'none on file'}</span>{' '}
                      <span style={{ color: 'var(--pcb-blue)', fontWeight: 800 }}>→</span>{' '}
                      <span style={{ fontWeight: 600 }}>{r.entered}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* Change-in-Contact ceremony — REUSED (verification + accounts) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={cardStyle}>
                <h5 style={cardH5}>
                  Verification method
                  {hasPhotoIdCapture && <span style={chipOk}> pre-filled from this session</span>}
                </h5>
                <select
                  aria-label="Verification method"
                  value={verCode}
                  onChange={(e) => setVerCode(e.target.value)}
                  style={{ width: '100%', padding: '7px 9px', border: '1px solid var(--ink-100)', borderRadius: 7, fontSize: 12.5 }}
                >
                  {VERIFICATION_METHODS.map((v) => (
                    <option key={v.code} value={v.code}>{v.code} — {v.label}</option>
                  ))}
                </select>
                {hasPhotoIdCapture && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 6 }}>
                    Auto-selected because this session captured and extracted a photo ID. Officer can override.
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <AuthorizedByField
                    id="cifcmp-authorized-by"
                    labelStyle={{ ...cardH5, display: 'block' }}
                    value={authorizedBy}
                    onChange={setAuthorizedBy}
                    required={initialsRequired}
                    prefill={authorizedByPrefill}
                    disabled={applying}
                  />
                </div>
              </div>
              <div style={cardStyle}>
                <h5 style={cardH5}>Verification document</h5>
                {hasPhotoIdCapture ? (
                  <span style={{ ...chipOk, display: 'inline-block' }}>✓ Session capture attached — photo ID</span>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>No session photo-ID capture — attach per procedure.</span>
                )}
                <div style={{ fontSize: 11.5, color: 'var(--ink-600)', margin: '6px 0 8px' }}>
                  The ID captured in this session doubles as the change-of-contact verification document — no second scan.
                </div>
                {addressFieldChecked && (
                  <>
                    <h5 style={cardH5}>
                      Apply the address change to these accounts ({cascade.filter((c) => c.selected).length}/{cascade.length})
                    </h5>
                    <div style={{ border: '1px solid var(--ink-100)', borderRadius: 8, maxHeight: 150, overflowY: 'auto' }}>
                      {cascade.length === 0 ? (
                        <div style={{ padding: '8px 11px', fontSize: 12, color: 'var(--ink-600)' }}>No related deposit accounts.</div>
                      ) : cascade.map((c) => (
                        <label key={c.acctId} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 11px', borderBottom: '1px solid var(--ink-50)', fontSize: 12.5, cursor: 'pointer' }}>
                          <input type="checkbox" checked={c.selected} onChange={() => toggleCascade(c.acctId)} />
                          <code style={{ background: 'var(--ink-50)', borderRadius: 4, padding: '0 5px', fontSize: 11.5 }}>{c.acctId}</code>
                          <span style={{ color: 'var(--ink-600)', fontWeight: 700, fontSize: 11 }}>
                            {ACCT_TYPE_LABEL[String(c.acctType)] ?? String(c.acctType)}
                          </span>
                        </label>
                      ))}
                    </div>
                    {cascade.length > 0 && (
                      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                        <span style={linkStyle} onClick={() => selectAllCascade(true)}>Select all</span>
                        <span style={linkStyle} onClick={() => selectAllCascade(false)}>Clear</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>
              This is the existing Change-in-Contact procedure (verification methods 0-5 + who
              authorized it, the custAcctRelInq cascade, custMod + acctMod + CustMsgAdd audit note) — not a
              new one. Unchecked rows keep their on-file values.
            </div>

            {/* Actions live in the pinned footer — one confirm, always visible
                (the buried in-panel action row was the "nothing happens"
                dead-end, 2026-07-03). Uncheck everything to link as-is. */}
          </div>
        </div>
      )}

      {/* ── NOT-A-MATCH panel ── */}
      {decision === 'new' && (
        <div style={{ marginTop: 'var(--sp-5)', border: '1px solid var(--info-line)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '11px 16px', borderBottom: '1px solid var(--ink-50)', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink-700)' }}>
            Not a match — continue adding as a new customer
          </div>
          <div style={{ padding: '13px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 13 }}>
              The full entered draft — every field, both captures, the OFAC and AML results — stays
              exactly as it was. The rejected-match decision is recorded as a session event
              (officer · timestamp · candidate CIF · match basis).
            </div>
            {matchedOnTaxId && (
              <Banner tone="err">
                <strong>The entered SSN/TIN matches CIF #{selectedRow?.cifId}.</strong> A new CIF can
                never share an existing CIF's tax id (the CIF number is a Core System key). To add a
                different person, the entered tax id will be <strong>cleared</strong> and you must enter
                a corrected, non-colliding value before re-adding.
              </Banner>
            )}
            {/* Confirm lives in the pinned footer — single action, always visible. */}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// --------------------------------------------------------------------------
// out-of-state helper (kept on a `state` namespace object for readability)
// --------------------------------------------------------------------------
const state = {
  outOfStateCheck(onFile: RaoPerson, draft: RaoPerson): boolean {
    const a = (onFile.state ?? '').toUpperCase();
    const b = (draft.state ?? '').toUpperCase();
    return Boolean(a && b && a !== b);
  },
};

// --------------------------------------------------------------------------
// presentational bits
// --------------------------------------------------------------------------
const thBase: React.CSSProperties = {
  background: 'var(--brand-fill-strong)', color: 'var(--on-brand)', textAlign: 'left', padding: '9px 12px',
  fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase',
};
const tdBase: React.CSSProperties = { padding: '9px 12px', borderTop: '1px solid var(--ink-50)', verticalAlign: 'top' };
const diffCell: React.CSSProperties = { background: 'var(--warn-bg)' };
const entCell: React.CSSProperties = { background: 'var(--info-bg)' };
const fileCell: React.CSSProperties = { background: 'var(--surface-subtle)', color: 'var(--ink-600)' };
const mutedStyle: React.CSSProperties = { color: 'var(--ink-400)', fontStyle: 'italic' };
const uprowStyle: React.CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-start', border: '1px solid var(--ink-100)', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, cursor: 'pointer' };
const cardStyle: React.CSSProperties = { border: '1px solid var(--ink-100)', borderRadius: 9, padding: '11px 13px', background: 'var(--surface-card, #fff)' };
const cardH5: React.CSSProperties = { margin: '0 0 6px', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-600)' };
const chipOk: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, background: 'var(--ok-bg)', color: 'var(--pcb-green)', borderRadius: 999, padding: '2px 8px', marginLeft: 4 };
const linkStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--pcb-blue)', fontWeight: 700, cursor: 'pointer' };

function StatePill({ state: s }: { state: CompareRow['state'] }): JSX.Element {
  const map: Record<CompareRow['state'], { bg: string; fg: string; label: string; border?: string }> = {
    match: { bg: 'var(--ok-bg)', fg: 'var(--pcb-green)', label: '✓ MATCH' },
    differ: { bg: 'var(--warn-bg)', fg: 'var(--warn-text)', label: '⚠ DIFFERS', border: '1px solid var(--warn-line)' },
    entonly: { bg: 'var(--info-bg)', fg: 'var(--pcb-blue)', label: '+ ENTERED ONLY' },
    fileonly: { bg: 'var(--surface-subtle)', fg: 'var(--ink-600)', label: 'ON FILE ONLY' },
  };
  const c = map[s];
  return (
    <span style={{ display: 'inline-block', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.05em', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap', background: c.bg, color: c.fg, ...(c.border ? { border: c.border } : {}) }}>
      {c.label}
    </span>
  );
}

function ArtifactChip({ ok, info, label }: { ok?: boolean; info?: boolean; label: string }): JSX.Element {
  const style: React.CSSProperties = ok
    ? { background: 'var(--ok-bg)', borderColor: 'var(--ok-line)', color: 'var(--pcb-green)', fontWeight: 600 }
    : info
      ? { background: 'var(--info-bg)', borderColor: 'var(--info-line)', color: 'var(--pcb-blue)', fontWeight: 600 }
      : { background: 'var(--ink-50)', borderColor: 'var(--ink-100)', color: 'var(--ink-600)' };
  return (
    <span style={{ display: 'inline-block', fontSize: 11, border: '1px solid', borderRadius: 999, padding: '3px 10px', ...style }}>
      {ok ? '✓ ' : ''}{label}
    </span>
  );
}
